'use client';

import { useEffect, useRef } from 'react';

import type {
  MatchUpdatedDto,
  MessageDto,
  MessagesReadDto,
  PresenceDto,
  TypingDto,
  ChatAccessDto,
} from '@/src/entities/chats/api/dto';
import { useAuthStore } from '@/src/shared/model/auth-store';

import { chatSocket } from './socket';

type ChatSocketHandlers = {
  onMessage: (message: MessageDto) => void;
  onRead: (payload: MessagesReadDto) => void;
  onMatch: (payload: MatchUpdatedDto) => void;
  onPresence: (payload: PresenceDto) => void;
  onTyping: (payload: TypingDto) => void;
  onMessageUpdated: (message: MessageDto) => void;
  onAccess: (payload: ChatAccessDto) => void;
};

export function useChatSocket(handlers: ChatSocketHandlers) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!isAuthenticated) {
      chatSocket.disconnect();
      return;
    }

    const handleMessage = (message: MessageDto) => {
      handlersRef.current.onMessage(message);
    };
    const handleRead = (payload: MessagesReadDto) => {
      handlersRef.current.onRead(payload);
    };
    const handleMatch = (payload: MatchUpdatedDto) => {
      handlersRef.current.onMatch(payload);
    };
    const handlePresence = (payload: PresenceDto) => {
      handlersRef.current.onPresence(payload);
    };
    const handleTyping = (payload: TypingDto) => {
      handlersRef.current.onTyping(payload);
    };
    const handleMessageUpdated = (message: MessageDto) => {
      handlersRef.current.onMessageUpdated(message);
    };
    const handleAccess = (payload: ChatAccessDto) => {
      handlersRef.current.onAccess(payload);
    };
    const handleError = (error: Error) => {
      if (error.message === 'unauthorized') {
        chatSocket.disconnect();
      }
    };

    chatSocket.on('message:created', handleMessage);
    chatSocket.on('messages:read', handleRead);
    chatSocket.on('match:updated', handleMatch);
    chatSocket.on('presence:updated', handlePresence);
    chatSocket.on('typing:updated', handleTyping);
    chatSocket.on('message:updated', handleMessageUpdated);
    chatSocket.on('chat:access', handleAccess);
    chatSocket.on('connect_error', handleError);

    if (!chatSocket.connected) {
      chatSocket.connect();
    }

    return () => {
      chatSocket.off('message:created', handleMessage);
      chatSocket.off('messages:read', handleRead);
      chatSocket.off('match:updated', handleMatch);
      chatSocket.off('presence:updated', handlePresence);
      chatSocket.off('typing:updated', handleTyping);
      chatSocket.off('message:updated', handleMessageUpdated);
      chatSocket.off('chat:access', handleAccess);
      chatSocket.off('connect_error', handleError);
      chatSocket.disconnect();
    };
  }, [isAuthenticated]);
}
